import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  setIsPrimaryDisconnected,
  setIsStreamingDisconnected,
  setIsStreamingReAttempting,
  setStreamingToken,
  setUiErrorScreen
} from "store/app.slice";
import { getEndpoint, ProctoringRoute } from "routes/route";
import {
  ClientErrorTypes,
  ErrorTypes,
  firebaseCollections,
  ScreenShareStatus,
  SessionScope,
  SessionStatus,
  StreamingProvider
} from "globals/enums";
import { useNavigate } from "react-router";
import { handleError } from "helpers/sentry";
import { AlertName } from "helpers/alert-type";
import { alertHelper } from "helpers/alert-helper";
import { AlertLogConfig } from "services/session-token";
import { RecordingService } from "globals/interfaces";
import { TwilioService } from "services/twilio";
import { LivekitService } from "services/livekit";
import { useStreamParticipantMutation } from "services/stream";
import { MediaContext } from "context/MediaContext";
import { LocalTrackPublication as TwilioLocalTrackPublication } from "twilio-video";
import { LocalTrackPublication as LiveKitLocalTrackPublication } from "livekit-client";
import { detectMob } from "helpers/browser-helper";
import { getSystemTime } from "helpers/common";
import { getDatabase, ref, update } from "firebase/database";

const useRecorder = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { mediaStream, displayStream } = useContext(MediaContext);
  const [createStream] = useStreamParticipantMutation();

  const configuration = useAppSelector((state) => state.app.configuration);
  const session = useAppSelector((state) => state.app.session);
  const streamToken = useAppSelector((state) => state.app.streamToken);
  const isPrimaryDisconnected = useAppSelector((state) => state.app.isPrimaryDisconnected);
  const isStreamingReAttempting = useAppSelector((state) => state.app.isStreamingReAttempting);
  const isVideoStreamingEnabled = useAppSelector((state) => state.app.isVideoStreamingEnabled);
  const isScreenRecordingEnabled = useAppSelector((state) => state.app.isScreenRecordingEnabled);
  const screenShareStatus = useAppSelector((state) => state.screenShare.screenShareStatus);
  const participantGroupId = useAppSelector((state) => state.app.channelData.participant_group_id);
  const organizationId = useAppSelector((state) => state.app.organizationId);
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const isCandidateAuthSession = useAppSelector(
    (state) => state.app.session?.session_type === SessionScope.CandidateAuth
  );
  const isNotPlatformStreaming =
    configuration.streaming_provider !== StreamingProvider.PlatformStreaming;
  const screenSharingEnabled = useMemo(
    () => !isCandidateAuthSession && isScreenRecordingEnabled && isNotPlatformStreaming,
    [isCandidateAuthSession, isScreenRecordingEnabled, isNotPlatformStreaming]
  );
  const [streamRoom, setStreamRoom] = useState<any>(null);
  const publishedScreenTrackRef = useRef<
    TwilioLocalTrackPublication | LiveKitLocalTrackPublication | null
  >(null);
  const connectionState = useRef({
    retryAttempts: 2,
    allowedReconnections: 6,
    showDisconnection: true,
    isReconnecting: false,
    reconnectAttemptCount: 0,
    lastDisconnectAt: 0,
    lastReconnectStartAt: 0
  });

  const recordingService: RecordingService = useMemo(() => {
    if (configuration.streaming_provider === StreamingProvider.Livekit) {
      return new LivekitService();
    }
    if (configuration.streaming_provider === StreamingProvider.Twilio) {
      return new TwilioService();
    }
  }, [configuration.streaming_provider]);

  const handleReconnection = async (token: string) => {
    if (connectionState.current.isReconnecting) return;
    connectionState.current.isReconnecting = true;

    try {
      if (!connectionState.current.lastDisconnectAt) {
        connectionState.current.lastDisconnectAt = Date.now();
      }

      if (
        connectionState.current.retryAttempts <= 0 ||
        connectionState.current.allowedReconnections <= 0
      ) {
        dispatch(setIsStreamingReAttempting({ isStreamingReAttempting: false }));
        dispatch(setIsStreamingDisconnected({ isStreamingDisconnected: true }));
        dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.StreamingReconnection }));
        navigate(getEndpoint(ProctoringRoute.Error));
        return;
      }

      connectionState.current.reconnectAttemptCount += 1;
      const currentAttempt = connectionState.current.reconnectAttemptCount;
      const delay =
        currentAttempt === 1
          ? 0
          : Math.min(2000 * Math.pow(2, currentAttempt - 2), 10000);

      dispatch(setIsStreamingReAttempting({ isStreamingReAttempting: true }));

      if (delay > 0) {
        console.log(
          `[Streaming] reconnect attempt ${currentAttempt} waiting ${delay}ms (offline ${
            Date.now() - connectionState.current.lastDisconnectAt
          }ms)`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        console.log("[Streaming] reconnect attempt 1 starting immediately");
      }

      connectionState.current.lastReconnectStartAt = Date.now();

      connectionState.current.retryAttempts--;
      await connectToStream(token);
    } finally {
      connectionState.current.isReconnecting = false;
    }
  };

  const connectToStream = async (token: string) => {
    recordingService.disconnect();
    setStreamRoom(null);

    recordingService.connect({
      tracks: isVideoStreamingEnabled ? mediaStream.getTracks() : [],
      isAudio: configuration.audio_check,
      streamingToken: token,
      orgId: organizationId?.toString(),
      onDisconnect: async () => {
        console.log("Streaming disconnected");
        if (connectionState.current.showDisconnection) {
          connectionState.current.reconnectAttemptCount = 0;
          connectionState.current.lastDisconnectAt = Date.now();
          handleError("Streaming disconnected", ClientErrorTypes.StreamDisonnectError);
          await handleReconnection(token);
        }
      },
      trackPublished: (room) => {
        dispatch(setIsStreamingReAttempting({ isStreamingReAttempting: false }));
        dispatch(setIsPrimaryDisconnected({ isPrimaryDisconnected: false }));
        setStreamRoom(room);
        connectionState.current.showDisconnection = true;

        alertHelper(alertConfig).raiseAlert(AlertName.RecordingStarted, dispatch);

        if (connectionState.current.retryAttempts !== 2) {
          connectionState.current.allowedReconnections--;
          connectionState.current.retryAttempts = 2;
          const reconnectDuration = connectionState.current.lastDisconnectAt
            ? Date.now() - connectionState.current.lastDisconnectAt
            : 0;
          const connectionDuration = connectionState.current.lastReconnectStartAt
            ? Date.now() - connectionState.current.lastReconnectStartAt
            : 0;
          const attemptsTaken = connectionState.current.reconnectAttemptCount;
          console.log(
            `[Streaming] reconnect succeeded after ${attemptsTaken} attempt(s); total offline ${reconnectDuration}ms; last connect call ${connectionDuration}ms`
          );
          connectionState.current.reconnectAttemptCount = 0;
          connectionState.current.lastDisconnectAt = 0;
          connectionState.current.lastReconnectStartAt = 0;
          console.log("Reconnected", 6 - connectionState.current.allowedReconnections);
          alertHelper(alertConfig).raiseAlert(
            AlertName.VideoStreamReconnect,
            dispatch,
            "Custom Reconnect"
          );
        }
      },
      onTrackPublishFailed: async (e) => {
        console.log(`Track publish failed, ${e}`);
        if (connectionState.current.showDisconnection) {
          handleError(e, ClientErrorTypes.StreamTrackPublishError);
          await handleReconnection(token);
        }
      },
      onNetworkQualityChange: (goodBandwidth: boolean) => {
        alertHelper(alertConfig).raiseAlert(
          goodBandwidth ? AlertName.CandidateGoodBandwidth : AlertName.CandidateLowBandwidth,
          dispatch
        );
      },
      onConnectionFailure: async (e) => {
        console.log("Streaming Connection Failure", e);
        if (connectionState.current.showDisconnection) {
          handleError("Streaming Connection Failure", ClientErrorTypes.StreamConnectionFailedError);
          await handleReconnection(token);
        }
      },
      onReconnecting: () => {
        alertHelper(alertConfig).raiseAlert(
          AlertName.VideoStreamReconnect,
          dispatch,
          "Reconnecting"
        );
      },
      onReconnected: () => {
        alertHelper(alertConfig).raiseAlert(
          AlertName.VideoStreamReconnect,
          dispatch,
          "Reconnected"
        );
      },
      onAudioVideoTrackPublished: async (trackInfo: any, streamingProvider: StreamingProvider) => {
        const roomParticipantRef = ref(
          getDatabase(),
          `${firebaseCollections.RoomParticipant}/${streamingProvider}_${session?.session_id}/tracks/${trackInfo?.participantSid}`
        );
        await update(roomParticipantRef, {
          ...trackInfo.tracks,
          timestamp: getSystemTime().toISOString()
        });
      }
    });
  };

  const startScreenRecording = async () => {
    if (!configuration.screen_recording) return;

    const screenTrack = recordingService.createLocalVideoTrack(
      displayStream.getTracks()[0],
      "screenshare"
    );

    publishedScreenTrackRef.current = await recordingService.publishTrack(
      screenTrack,
      "screenshare"
    );
  };
  const stopScreenRecording = useCallback(() => {
    if (!publishedScreenTrackRef.current) {
      return;
    }

    const previousShowDisconnection = connectionState.current.showDisconnection;
    connectionState.current.showDisconnection = false;

    try {
      recordingService?.unpublishTrack(publishedScreenTrackRef.current);
    } finally {
      connectionState.current.showDisconnection =
        typeof previousShowDisconnection === "boolean" ? previousShowDisconnection : true;
      publishedScreenTrackRef.current = null;
    }
  }, [recordingService]);

  const startStreaming = async () => {
    try {
      const stream = await createStream({
        participant_group_id: participantGroupId
      }).unwrap();
      dispatch(setStreamingToken({ streamToken: stream.token }));
      await connectToStream(stream.token);
    } catch (e) {
      handleError(e, ClientErrorTypes.StreamExceptionError);
      dispatch(setIsStreamingDisconnected({ isStreamingDisconnected: true }));
      dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.StreamingReconnection }));
      navigate(getEndpoint(ProctoringRoute.Error));
    }
  };
  const stopStreaming = useCallback(() => {
    connectionState.current.showDisconnection = false;
    setStreamRoom(null);
    recordingService?.disconnect();
  }, [recordingService]);

  useEffect(() => {
    if (!recordingService) return;
    if ((isVideoStreamingEnabled || isScreenRecordingEnabled) && !isCandidateAuthSession) {
      (async () => await startStreaming())();
    } else if (streamRoom) {
      stopStreaming();
    }
  }, [
    recordingService,
    isVideoStreamingEnabled || isScreenRecordingEnabled,
    isCandidateAuthSession
  ]);

  useEffect(() => {
    (async () => {
      if (recordingService && isVideoStreamingEnabled && streamRoom && mediaStream?.active) {
        await recordingService.flipVideo(mediaStream.getTracks());
      }
    })();
  }, [mediaStream?.active, isVideoStreamingEnabled]);

  useEffect(() => {
    if (
      !streamRoom ||
      !screenSharingEnabled ||
      !recordingService ||
      (detectMob() && configuration.is_mobile_supported)
    )
      return;

    if (screenShareStatus === ScreenShareStatus.ScreenShareStarted) {
      (async () => await startScreenRecording())();
    } else if (screenShareStatus === ScreenShareStatus.ScreenShareStopped) {
      stopScreenRecording();
    }
  }, [screenShareStatus, streamRoom, screenSharingEnabled]);

  useEffect(() => {
    if (isStreamingReAttempting || !isPrimaryDisconnected || !recordingService) return;
    if (
      (isVideoStreamingEnabled || isScreenRecordingEnabled) &&
      !isCandidateAuthSession &&
      streamToken
    ) {
      (async () => await handleReconnection(streamToken))();
    }
  }, [isPrimaryDisconnected]);

  const sessionStatus = useAppSelector((state) => state.app.sessionStatus);

  useEffect(() => {
    if (
      sessionStatus === SessionStatus.Completed ||
      sessionStatus === SessionStatus.Terminated
    ) {
      stopScreenRecording();
      stopStreaming();
    }
  }, [sessionStatus, stopScreenRecording, stopStreaming]);

  return [recordingService];
};

export default useRecorder;
